import type { SupabaseClient } from '@supabase/supabase-js'
import type {
  IConversationRepository,
  IResponseDataReader,
  Conversation,
  QuantitativeResponse,
  InformationByte,
  ConversationStatus,
  Sentiment,
  WidgetType,
} from '@knownow/domain'
import { NotFoundError } from '@knownow/domain'
import type { Database, Json } from '@knownow/db'

type ConversationRow = Database['public']['Tables']['conversations']['Row']
type InformationByteRow = Database['public']['Tables']['information_bytes']['Row']
type QuantitativeResponseRow =
  Database['public']['Tables']['quantitative_responses']['Row']

export class SupabaseConversationRepository
  implements IConversationRepository, IResponseDataReader
{
  constructor(
    private readonly client: SupabaseClient<Database>,
    private readonly organizationId: string,
  ) {}

  async getById(id: string): Promise<Conversation> {
    const { data, error } = await this.client
      .from('conversations')
      .select('*')
      .eq('id', id)
      .single()

    if (error !== null || data === null) {
      throw new NotFoundError('Conversation', id)
    }

    return this.conversationToDomain(data)
  }

  async getConversationForRespondent(
    planId: string,
    respondentId: string,
  ): Promise<Conversation | null> {
    const { data, error } = await this.client
      .from('conversations')
      .select('*')
      .eq('plan_id', planId)
      .eq('respondent_id', respondentId)
      .maybeSingle()

    if (error !== null) {
      throw error
    }
    return data === null ? null : this.conversationToDomain(data)
  }

  async listByPlan(planId: string): Promise<readonly Conversation[]> {
    const { data, error } = await this.client
      .from('conversations')
      .select('*')
      .eq('plan_id', planId)
      .order('started_at', { ascending: false })

    if (error !== null) {
      throw error
    }
    return (data ?? []).map((row) => this.conversationToDomain(row))
  }

  async save(conversation: Conversation): Promise<string> {
    const payload = {
      plan_id: conversation.planId,
      respondent_id: conversation.respondentId,
      status: conversation.status,
      organization_id: this.organizationId,
      started_at: conversation.startedAt.toISOString(),
      completed_at: conversation.completedAt?.toISOString() ?? null,
      created_at: conversation.createdAt.toISOString(),
    }

    const upsertPayload = conversation.id ? { id: conversation.id, ...payload } : payload
    const { data, error } = await this.client
      .from('conversations')
      .upsert(upsertPayload)
      .select('id')
      .single()

    if (error !== null) {
      throw error
    }
    return data.id
  }

  async saveQuantitativeResponse(response: QuantitativeResponse): Promise<string> {
    const { data, error } = await this.client
      .from('quantitative_responses')
      .insert({
        conversation_id: response.conversationId,
        information_need_id: response.informationNeedId,
        widget_type: response.widgetType,
        numeric_value: response.numericValue,
        selected_options: response.selectedOptions as unknown as Json | null,
        ordered_items: response.orderedItems as unknown as Json | null,
        organization_id: this.organizationId,
        created_at: response.createdAt.toISOString(),
      })
      .select('id')
      .single()

    if (error !== null) {
      throw error
    }
    return data.id
  }

  async saveInformationByte(byte: InformationByte): Promise<string> {
    const { data, error } = await this.client
      .from('information_bytes')
      .insert({
        conversation_id: byte.conversationId,
        information_need_id: byte.informationNeedId,
        content: byte.content,
        source_quote: byte.sourceQuote,
        sentiment: byte.sentiment,
        organization_id: this.organizationId,
        created_at: byte.createdAt.toISOString(),
      })
      .select('id')
      .single()

    if (error !== null) {
      throw error
    }
    return data.id
  }

  // ─── IResponseDataReader ─────────────────────────────────────────────────

  async listInformationBytesByConversation(
    conversationId: string,
  ): Promise<readonly InformationByte[]> {
    const { data, error } = await this.client
      .from('information_bytes')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true })

    if (error !== null) throw error
    return (data ?? []).map((row) => this.byteToDomain(row))
  }

  async listQuantitativeResponsesByConversation(
    conversationId: string,
  ): Promise<readonly QuantitativeResponse[]> {
    const { data, error } = await this.client
      .from('quantitative_responses')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true })

    if (error !== null) throw error
    return (data ?? []).map((row) => this.responseToDomain(row))
  }

  async listCompletedConversationsByPlan(
    planId: string,
  ): Promise<readonly Conversation[]> {
    const { data, error } = await this.client
      .from('conversations')
      .select('*')
      .eq('plan_id', planId)
      .eq('status', 'completed')
      .order('completed_at', { ascending: true })

    if (error !== null) throw error
    return (data ?? []).map((row) => this.conversationToDomain(row))
  }

  async listInformationBytesByPlan(
    planId: string,
  ): Promise<readonly InformationByte[]> {
    // Inner-join through `conversations` so RLS still gates the outer query.
    const { data, error } = await this.client
      .from('information_bytes')
      .select('*, conversations!inner(plan_id)')
      .eq('conversations.plan_id', planId)
      .order('created_at', { ascending: true })

    if (error !== null) throw error
    return (data ?? []).map((row) => this.byteToDomain(row as InformationByteRow))
  }

  async listQuantitativeResponsesByPlan(
    planId: string,
  ): Promise<readonly QuantitativeResponse[]> {
    const { data, error } = await this.client
      .from('quantitative_responses')
      .select('*, conversations!inner(plan_id)')
      .eq('conversations.plan_id', planId)
      .order('created_at', { ascending: true })

    if (error !== null) throw error
    return (data ?? []).map((row) =>
      this.responseToDomain(row as QuantitativeResponseRow),
    )
  }

  // ─── Mappers ─────────────────────────────────────────────────────────────

  private conversationToDomain(row: ConversationRow): Conversation {
    return {
      id: row.id,
      planId: row.plan_id,
      respondentId: row.respondent_id,
      status: row.status as ConversationStatus,
      startedAt: new Date(row.started_at),
      completedAt: row.completed_at !== null ? new Date(row.completed_at) : null,
      createdAt: new Date(row.created_at),
    }
  }

  private byteToDomain(row: InformationByteRow): InformationByte {
    return {
      id: row.id,
      conversationId: row.conversation_id,
      informationNeedId: row.information_need_id,
      content: row.content,
      sourceQuote: row.source_quote,
      sentiment: row.sentiment as Sentiment | null,
      createdAt: new Date(row.created_at),
    }
  }

  private responseToDomain(row: QuantitativeResponseRow): QuantitativeResponse {
    return {
      id: row.id,
      conversationId: row.conversation_id,
      informationNeedId: row.information_need_id,
      widgetType: row.widget_type as WidgetType,
      numericValue:
        typeof row.numeric_value === 'number'
          ? row.numeric_value
          : row.numeric_value === null
            ? null
            : Number(row.numeric_value),
      selectedOptions: (row.selected_options as unknown as readonly string[] | null) ?? null,
      orderedItems: (row.ordered_items as unknown as readonly string[] | null) ?? null,
      createdAt: new Date(row.created_at),
    }
  }
}
